import type {
  BoardBuilderDurationStatProjections,
  BoardBuilderUpdateProjection
} from '@library/types/boardBuilder';
import {
  findBoardBuilderProjectionIndex,
  getValidationErrors
} from '@bo-utils/boardBuilder';
import type { Updater } from 'use-immer';
import { copyProjectionSplit } from '@bo-utils/copyProjections';
import { getLeagueDurationAttributes } from '@bo-utils/leagues';
import { isStatusRemoved } from '@library/utils';
import { onAutoTradeInGameChange } from '@bo-utils/projection';

interface BoardBuilderUpdateProjectionUpdater
  extends BoardBuilderUpdateProjection {
  setDurationStatProjections: Updater<BoardBuilderDurationStatProjections>;
}

export const updateProjection = ({
  durations,
  initialProjection,
  key,
  leagueInfo: leagueData,
  mainDurationId: firstDurationId,
  projectionId,
  value,
  setDurationStatProjections
}: BoardBuilderUpdateProjectionUpdater) => {
  const { durationId, playerForeignId, gameId, statTypeId } =
    initialProjection ?? {};

  setDurationStatProjections((draft) => {
    if (
      !durationId ||
      !statTypeId ||
      !draft[durationId] ||
      !draft[durationId].statTypes[statTypeId]
    ) {
      return;
    }

    const draftDurationStatTypeProjections =
      draft[durationId].statTypes[statTypeId].projections;

    const draftDurationStatTypeProjectionIndex =
      findBoardBuilderProjectionIndex({
        projectionId,
        projections: draftDurationStatTypeProjections
      });

    if (draftDurationStatTypeProjectionIndex === -1) {
      return;
    }
    draftDurationStatTypeProjections[draftDurationStatTypeProjectionIndex][
      key
    ] = value;

    // If update key is autoTrade or inGame,
    // then disable inGame when autoTrade is disabled and vice versa
    if (['autoTrade', 'inGame'].includes(key)) {
      const projection =
        draftDurationStatTypeProjections[draftDurationStatTypeProjectionIndex];
      const { autoTrade, inGame } = projection;

      const updatedProjection = onAutoTradeInGameChange(
        autoTrade,
        inGame,
        key === 'autoTrade',
        true
      );

      Object.assign(projection, updatedProjection);
    }

    // If the update key is not status and the status is in removed status,
    // change the status to pre_game
    if (
      key !== 'status' &&
      isStatusRemoved(
        draftDurationStatTypeProjections[draftDurationStatTypeProjectionIndex]
          .status
      )
    ) {
      draftDurationStatTypeProjections[
        draftDurationStatTypeProjectionIndex
      ].status = 'pre_game';
    }

    draftDurationStatTypeProjections[
      draftDurationStatTypeProjectionIndex
    ].errors = getValidationErrors({
      key,
      projection:
        draftDurationStatTypeProjections[draftDurationStatTypeProjectionIndex]
    });

    // If the duration of this updating projection is Full/1st inng and the update key is projectionValue,
    // Find and Update the Sub League/Duration projection's split projection Value, only
    // if the sub league/duration projection is in removed status

    if (durationId === firstDurationId && key === 'projectionValue') {
      durations.forEach((duration) => {
        const currentDurationId = duration.leagueProjectionFilterId;

        if (currentDurationId === durationId) {
          return;
        }

        const projections =
          draft[currentDurationId].statTypes[statTypeId]?.projections;

        if (!projections || projections.length < 1) {
          return;
        }

        const currentDurationStatProjectionIndex = projections.findIndex(
          (projection) =>
            projection.playerForeignId === playerForeignId &&
            projection.gameId === gameId &&
            isStatusRemoved(projection.status)
        );

        if (currentDurationStatProjectionIndex !== -1) {
          if (!value) {
            projections[currentDurationStatProjectionIndex].projectionValue =
              '';

            return;
          }

          const { name: leagueName } = getLeagueDurationAttributes({
            leagueInfo: leagueData,
            durationId: currentDurationId
          });

          projections[currentDurationStatProjectionIndex].projectionValue =
            Number(value) * (copyProjectionSplit[leagueName] ?? 1);
        }
      });
    }
  });
};
